/**
 * Toast & Popup Manager
 */

import * as PIXI from 'pixi.js';
import { THEME, LAYOUT } from './theme.js';
import { makeText } from './UIHelpers.js';

interface ToastItem {
  container: PIXI.Container;
  timer: number;
  targetY: number;
}

export class PopupManager {
  private stage: PIXI.Container;
  private toasts: ToastItem[] = [];
  private screenW: number;

  constructor(stage: PIXI.Container, screenW: number) {
    this.stage = stage;
    this.screenW = screenW;
  }

  resize(w: number): void {
    this.screenW = w;
  }

  showToast(message: string, color: number = THEME.accent.green, duration = 2.5): void {
    const w = Math.min(this.screenW - 32, 320);
    const h = 44;
    const x = (this.screenW - w) / 2;

    const container = new PIXI.Container();
    container.x = x;
    container.y = -h;
    container.alpha = 0;

    const gfx = new PIXI.Graphics();
    // Glass background
    gfx.beginFill(THEME.bg.glass, 0.12);
    gfx.drawRoundedRect(0, 0, w, h, h / 2);
    gfx.endFill();
    // Color accent strip on left
    gfx.beginFill(color, 0.8);
    gfx.drawRoundedRect(0, 0, 6, h, 3);
    gfx.endFill();
    // Border
    gfx.lineStyle(1, THEME.border.glass, 0.1);
    gfx.drawRoundedRect(0, 0, w, h, h / 2);
    gfx.lineStyle(0);
    container.addChild(gfx);

    const txt = makeText(message, 13, THEME.text.primary);
    txt.x = 16;
    txt.anchor.set(0, 0.5);
    txt.y = h / 2;
    container.addChild(txt);

    this.stage.addChild(container);

    // Push down existing toasts
    const targetY = LAYOUT.safeTop + 12 + this.toasts.length * (h + 8);
    this.toasts.push({ container, timer: duration, targetY });
  }

  showAchievement(name: string): void {
    this.showToast(`🏅 ${name}`, THEME.accent.gold, 3);
  }

  showVariety(name: string): void {
    this.showToast(`☕ ${name}`, THEME.accent.brown, 3);
  }

  update(dt: number): void {
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const toast = this.toasts[i];
      toast.timer -= dt;

      // Animate in
      if (toast.container.alpha < 1) {
        toast.container.alpha = Math.min(1, toast.container.alpha + dt * 4);
      }

      // Move to target Y
      const dy = toast.targetY - toast.container.y;
      toast.container.y += dy * Math.min(1, dt * 8);

      // Fade out
      if (toast.timer <= 0.5) {
        toast.container.alpha = Math.max(0, toast.timer / 0.5);
      }

      // Remove
      if (toast.timer <= 0) {
        this.stage.removeChild(toast.container);
        toast.container.destroy({ children: true });
        this.toasts.splice(i, 1);
      }
    }
  }
}
